/**
 * useStock — camada de dados (TanStack Query) do módulo de Estoque.
 *
 * Estoque só existe para PRODUTOS FÍSICOS rastreados (type='product' +
 * track_stock). O saldo (products.stock_quantity) é a FONTE DE VERDADE do
 * servidor: só muda pela RPC apply_stock_movement — nunca gravamos o saldo
 * absoluto pelo cliente.
 *
 * Offline: registerMovement enfileira O MOVIMENTO (delta+motivo+refs) e atualiza
 * o saldo do cache de forma otimista; no sync o worker chama a RPC, que recomputa
 * a partir do saldo real. Ver [[orcamigo-offline-sync-uuid-bug]].
 */
import { useCallback, useEffect, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { supabase } from '@/integrations/supabase/client';
import { checkNetworkStatus } from '@/hooks/useNetworkStatus';
import { useOfflineSync } from '@/hooks/useOfflineSync';
import {
  cacheCatalog,
  getCachedCatalog,
  cacheStockMovements,
  getCachedStockMovements,
} from '@/lib/offlineDataCache';
import { filterLowStock } from '@/lib/stock';
import { isStockableUnit, parentIdsWithVariants } from '@/lib/variants';
import type { StockReason } from '@/types/database';
import { toast } from 'sonner';
import { fetchAllPages } from '@/lib/reportPaging';
import {
  stockUnitMatchesReportFilters,
  type ReportFilterState,
} from '@/lib/reportFilters';
import {
  DEFAULT_STOCK_REPORT_SORT,
  sortStockUnits,
  stockSortServerColumn,
  type StockReportSort,
} from '@/lib/stockReport';

const STOCK_PRODUCTS_KEY = 'stock-products';
const STOCK_MOVEMENTS_KEY = 'stock-movements';

/** Uma FK que possa carregar um id offline_ é nula num write ONLINE. */
function nullIfOffline(value?: string | null): string | null {
  if (!value) return null;
  return String(value).startsWith('offline_') ? null : value;
}

export interface RegisterMovementInput {
  productId: string;
  /** Delta assinado (entrada +, saída −). */
  delta: number;
  reason: StockReason;
  quoteId?: string | null;
  serviceOrderId?: string | null;
  note?: string | null;
}

/**
 * Produtos da empresa (lista completa, cacheada no catálogo) + derivações de
 * estoque (rastreados, estoque baixo). Compartilha o cache com o Catálogo, então
 * um movimento reflete nas duas telas.
 */
export interface StockReportQueryOptions {
  filters?: ReportFilterState;
  sort?: StockReportSort;
  selectedParentId?: string | null;
}

export function useStock(options: StockReportQueryOptions = {}) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const reportFilters = options.filters;
  const reportSort = options.sort ?? DEFAULT_STOCK_REPORT_SORT;
  const selectedProductId = reportFilters?.productId ?? null;
  const selectedParentId = options.selectedParentId ?? null;
  const isReportQuery = Boolean(options.filters || options.sort || options.selectedParentId);
  const stockQueryKey = isReportQuery
    ? [
        STOCK_PRODUCTS_KEY,
        'report',
        companyId,
        selectedProductId,
        selectedParentId,
        reportSort.field,
        reportSort.direction,
      ]
    : [STOCK_PRODUCTS_KEY, companyId];

  const query = useQuery({
    queryKey: stockQueryKey,
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => {
      if (!user?.id) return undefined;
      const cached = (getCachedCatalog(user.id) ?? []) as any[];
      const scoped = reportFilters
        ? cached.filter((row) =>
            stockUnitMatchesReportFilters(row, reportFilters)
            || Boolean(selectedParentId && row?.id === selectedParentId))
        : cached;
      const rows = sortStockUnits(scoped, reportSort) as any[];
      return isReportQuery ? { rows, truncated: false } : rows;
    },
    queryFn: async (): Promise<any> => {
      if (!user?.id || !companyId) {
        return isReportQuery ? { rows: [], truncated: false } : [];
      }
      const result = await fetchAllPages<any>((start, end) => {
        let request: any = supabase
          .from('products')
          .select('*')
          .eq('company_id', companyId);
        if (selectedProductId && selectedParentId) {
          request = request.or(`id.eq.${selectedProductId},id.eq.${selectedParentId}`);
        } else if (selectedProductId) {
          request = request.eq('id', selectedProductId);
        }
        return request
          .order(stockSortServerColumn(reportSort.field), {
            ascending: reportSort.direction === 'asc',
            nullsFirst: false,
          })
          .order('id', { ascending: true })
          .range(start, end);
      });
      const server = result.rows as any[];
      // Preserva itens criados offline ainda não sincronizados (como o CatalogPage),
      // para não sumirem da lista do catálogo ao passar pela tela de Estoque.
      const existing = (getCachedCatalog(user.id) || []) as any[];
      const serverIds = new Set(server.map((p) => p.id));
      const offlinePending = existing.filter(
        (p: any) =>
          p._offline_pending === true
          && String(p.id).startsWith('offline_')
          && !serverIds.has(p.id)
          && (!reportFilters || stockUnitMatchesReportFilters(p, reportFilters)),
      );
      const merged = sortStockUnits([...server, ...offlinePending], reportSort) as any[];
      if (!selectedProductId) cacheCatalog(user.id, merged);
      return isReportQuery ? { rows: merged, truncated: result.truncated } : merged;
    },
  });

  // Refetch quando o catálogo ou o estoque muda em outra tela.
  useEffect(() => {
    const handler = () => queryClient.invalidateQueries({ queryKey: [STOCK_PRODUCTS_KEY] });
    window.addEventListener('catalog-updated', handler);
    window.addEventListener('stock-updated', handler);
    return () => {
      window.removeEventListener('catalog-updated', handler);
      window.removeEventListener('stock-updated', handler);
    };
  }, [queryClient, companyId]);

  const products = (
    Array.isArray(query.data) ? query.data : query.data?.rows ?? []
  ) as any[];
  const productsTruncated = Array.isArray(query.data)
    ? false
    : Boolean(query.data?.truncated);

  // Com variações, a unidade estocável é a FILHA — nunca o pai. Um pai que
  // rastreava estoque antes de ganhar variações continua com track_stock=true
  // no banco até a conversão desligar; sem este filtro ele apareceria no
  // Estoque em dobro (ele + as filhas) e entraria na baixa de orçamento.
  const parentsWithVariants = useMemo(() => parentIdsWithVariants(products), [products]);

  const trackedProducts = useMemo(
    () => products.filter((p) => isStockableUnit(p, parentsWithVariants)),
    [products, parentsWithVariants],
  );
  const lowStock = useMemo(() => filterLowStock(trackedProducts), [trackedProducts]);
  const trackedIds = useMemo(() => new Set(trackedProducts.map((p) => String(p.id))), [trackedProducts]);

  /** Pais indexados por id — a tela de Estoque usa para mostrar "Camiseta › P". */
  const productById = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of products) map.set(String(p.id), p);
    return map;
  }, [products]);

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [STOCK_PRODUCTS_KEY] });
  }, [queryClient]);

  return {
    ...query,
    products,
    trackedProducts,
    lowStock,
    trackedIds,
    productById,
    productsTruncated,
    invalidate,
  };
}

/** Histórico de movimentos (razão) — todos da empresa, filtrável por produto. */
export function useStockMovements(productId?: string) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;

  const query = useQuery({
    queryKey: [STOCK_MOVEMENTS_KEY, companyId],
    enabled: Boolean(user?.id && companyId),
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    // Revalida na montagem como a query de PRODUTOS logo acima: as duas
    // alimentam a mesma tela e uma baixa feita em outro dispositivo tem que
    // aparecer no histórico junto com o saldo novo.
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => (user?.id ? getCachedStockMovements(user.id) ?? undefined : undefined),
    queryFn: async (): Promise<any[]> => {
      if (!user?.id || !companyId) return [];
      const { data, error } = await supabase
        .from('stock_movements')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      const rows = (data ?? []) as any[];
      // Sobrescreve o cache — descarta os movimentos otimistas (offline_) já sincronizados.
      cacheStockMovements(user.id, rows);
      return rows;
    },
  });

  const movements = useMemo(() => {
    const all = (query.data ?? []) as any[];
    return productId ? all.filter((m) => m.product_id === productId) : all;
  }, [query.data, productId]);

  return { ...query, movements };
}

/**
 * Movimentos de um INTERVALO de datas — a leitura do Relatório de Estoque.
 *
 * NÃO usa `useStockMovements`: aquele hook traz no máximo 500 linhas e existe
 * para o histórico de um produto na tela de Estoque. Num relatório por período
 * ele sairia truncado em silêncio e os totais ficariam errados sem nenhum erro
 * na tela.
 *
 * Duas coisas que este hook faz de propósito:
 * - **Pagina** (`range`), porque o PostgREST corta a resposta num teto próprio
 *   (1.000 linhas por padrão) — sem paginar, o corte seria invisível.
 * - Devolve `truncated` quando bate no teto de segurança, para a tela AVISAR em
 *   vez de mostrar um número incompleto como se fosse o total.
 *
 * O intervalo entra na queryKey junto do company_id: trocar de empresa ou de
 * período refaz a leitura em vez de reaproveitar os dados anteriores.
 */
const MOVEMENTS_PAGE_SIZE = 1000;
/** Teto de segurança: 50 páginas = 50 mil movimentos no período. */
const MOVEMENTS_MAX_PAGES = 50;

export interface StockMovementsRangeResult {
  movements: any[];
  /** Bateu no teto de páginas — a tela precisa avisar que há mais movimentos. */
  truncated: boolean;
}

export function useStockMovementsInRange(
  range: { from: Date; to: Date },
  options: { enabled?: boolean; filters?: ReportFilterState } = {},
) {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const productId = options.filters?.productId ?? null;

  const fromIso = range.from.toISOString();
  const toIso = range.to.toISOString();

  const query = useQuery({
    queryKey: [STOCK_MOVEMENTS_KEY, 'range', companyId, fromIso, toIso, productId],
    enabled: Boolean(user?.id && companyId) && options.enabled !== false,
    networkMode: 'offlineFirst',
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    // A query nasce DESABILITADA (sessão/empresa resolvem depois da montagem), então
    // `refetchOnMount` já passou quando ela habilita: sem o carimbo, o cache semeado
    // conta como FRESCO e a primeira busca nunca sai (#158).
    initialDataUpdatedAt: 0,
    initialData: () => {
      if (!user?.id) return undefined;
      const movements = (getCachedStockMovements(user.id) ?? []).filter((row: any) => {
        const createdAt = new Date(row?.created_at);
        return !Number.isNaN(createdAt.getTime())
          && createdAt >= range.from
          && createdAt <= range.to
          && (!productId || row?.product_id === productId);
      });
      return { movements, truncated: false };
    },
    queryFn: async (): Promise<StockMovementsRangeResult> => {
      if (!user?.id || !companyId) return { movements: [], truncated: false };

      const rows: any[] = [];
      let truncated = false;

      for (let page = 0; page < MOVEMENTS_MAX_PAGES; page++) {
        const start = page * MOVEMENTS_PAGE_SIZE;
        let request: any = supabase
          .from('stock_movements')
          .select('id, product_id, delta, balance_after, reason, quote_id, service_order_id, note, created_at')
          .eq('company_id', companyId)
          .gte('created_at', fromIso)
          .lte('created_at', toIso);
        if (productId) request = request.eq('product_id', productId);
        const { data, error } = await request
          .order('created_at', { ascending: false })
          .order('id', { ascending: true })
          .range(start, start + MOVEMENTS_PAGE_SIZE - 1);
        if (error) throw error;

        const batch = (data ?? []) as any[];
        rows.push(...batch);
        if (batch.length < MOVEMENTS_PAGE_SIZE) break;
        // A última página cheia dentro do teto significa que pode haver mais.
        if (page === MOVEMENTS_MAX_PAGES - 1) truncated = true;
      }

      return { movements: rows, truncated };
    },
  });

  return {
    ...query,
    movements: query.data?.movements ?? [],
    truncated: query.data?.truncated ?? false,
  };
}

/**
 * Registra um movimento de estoque. Online → chama a RPC apply_stock_movement
 * (fonte de verdade do saldo, permite negativo). Offline → enfileira o movimento
 * e atualiza o saldo do cache de forma otimista.
 */
export function useRegisterStockMovement() {
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const companyId = activeCompany?.id ?? null;
  const queryClient = useQueryClient();
  const { saveStockMovementOffline } = useOfflineSync();

  return useMutation({
    // offlineFirst: sem isto o TanStack PAUSA a mutation quando o app perde a
    // rede com a tela aberta, e o ramo offline nunca roda (CLAUDE.md #41).
    networkMode: 'offlineFirst' as const,
    mutationFn: async (input: RegisterMovementInput) => {
      if (!user?.id) throw new Error('Sem sessão');
      if (!companyId) throw new Error('Nenhuma empresa ativa');
      const delta = Number(input.delta);
      if (!Number.isFinite(delta) || delta === 0) throw new Error('Informe uma quantidade');

      const online = await checkNetworkStatus();

      if (!online) {
        saveStockMovementOffline({
          movement: {
            product_id: input.productId,
            delta,
            reason: input.reason,
            quote_id: input.quoteId ?? null,
            service_order_id: input.serviceOrderId ?? null,
            note: input.note ?? null,
          },
          userId: user.id,
          companyId,
        });
        queryClient.setQueryData([STOCK_PRODUCTS_KEY, companyId], getCachedCatalog(user.id) ?? []);
        return { offline: true as const };
      }

      // Online: o produto (e refs) precisam ser reais — um id offline_ não existe
      // no Postgres ainda.
      const productId = nullIfOffline(input.productId);
      if (!productId) throw new Error('Produto ainda não sincronizado. Tente novamente em instantes.');

      const { data, error } = await supabase.rpc('apply_stock_movement', {
        p_product_id: productId,
        p_delta: delta,
        p_reason: input.reason,
        p_quote_id: nullIfOffline(input.quoteId),
        p_service_order_id: nullIfOffline(input.serviceOrderId),
        p_note: input.note ?? null,
      });
      if (error) throw error;

      queryClient.invalidateQueries({ queryKey: [STOCK_PRODUCTS_KEY, companyId] });
      queryClient.invalidateQueries({ queryKey: [STOCK_MOVEMENTS_KEY, companyId] });
      window.dispatchEvent(new CustomEvent('catalog-updated'));
      window.dispatchEvent(new CustomEvent('stock-updated'));
      return { offline: false as const, balance: Number(data) };
    },
    onError: (e: any) =>
      toast.error(
        e?.message ? `Não foi possível registrar o movimento: ${e.message}` : 'Não foi possível registrar o movimento.',
      ),
  });
}
